

import React, { useEffect, useState } from "react";
import {Box,Container,Typography,Paper,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,Chip,IconButton,TextField,InputAdornment,
    Grid,Card,CardContent,Tabs,Tab,Dialog,DialogTitle,DialogContent,DialogActions,Button,CircularProgress,Alert,Divider,Stack,Select,MenuItem,
    FormControl,InputLabel,Tooltip,Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicationIcon from "@mui/icons-material/Medication";
import PaymentIcon from "@mui/icons-material/Payment";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { apiRequest } from "../../fetchSetup";

const CancelledOrders = () => {
  const [loading, setLoading] = useState(true);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [cancelledMedicineOrders, setCancelledMedicineOrders] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [filteredMedicineOrders, setFilteredMedicineOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [error, setError] = useState(null);

  const CANCELLED_ORDERS_API = process.env.REACT_APP_CANCELLED_ORDERS_API;

  useEffect(() => {
    fetchAllCancelledOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, dateFilter, paymentFilter, cancelledBookings, cancelledMedicineOrders]);

  const fetchAllCancelledOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch ALL cancelled orders in admin mode (no user_id or email required)
      const response = await apiRequest(`${CANCELLED_ORDERS_API}?admin=true`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch cancelled orders");
      }

      const data = await response.json();
      setCancelledBookings(data.cancelled_bookings || []);
      setCancelledMedicineOrders(data.cancelled_medicine_orders || []);
      console.log('Admin mode:', data.is_admin);
    } catch (err) {
      console.error(" Error fetching cancelled orders:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filteredB = [...cancelledBookings];
    let filteredM = [...cancelledMedicineOrders];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredB = filteredB.filter(
        (booking) =>
          booking.id?.toLowerCase().includes(query) ||
          booking.guest_name?.toLowerCase().includes(query) ||
          booking.guest_email?.toLowerCase().includes(query) ||
          booking.guest_phone?.includes(query) ||
          booking.service_type?.toLowerCase().includes(query)
      );

      filteredM = filteredM.filter(
        (order) =>
          order.id?.toLowerCase().includes(query) ||
          order.delivery_address?.full_name?.toLowerCase().includes(query) ||
          order.delivery_address?.phone?.includes(query) ||
          order.user_email?.toLowerCase().includes(query)
      );
    }

    // Payment filter
    if (paymentFilter !== "all") {
      filteredB = filteredB.filter(
        (booking) => booking.payment?.status === paymentFilter
      );
      filteredM = filteredM.filter(
        (order) => order.payment?.status === paymentFilter
      );
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const filterDate = new Date();

      if (dateFilter === "today") {
        filterDate.setHours(0, 0, 0, 0);
      } else if (dateFilter === "week") {
        filterDate.setDate(now.getDate() - 7);
      } else if (dateFilter === "month") {
        filterDate.setMonth(now.getMonth() - 1);
      }

      filteredB = filteredB.filter((booking) => {
        const bookingDate = new Date(booking.created_at || booking.date);
        return bookingDate >= filterDate;
      });

      filteredM = filteredM.filter((order) => {
        const orderDate = new Date(order.created_at);
        return orderDate >= filterDate;
      });
    }

    setFilteredBookings(filteredB);
    setFilteredMedicineOrders(filteredM);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return `₹${amount?.toFixed(2) || 0}`;
  };

  const handleViewDetails = (order, type) => {
    setSelectedOrder({ ...order, orderType: type });
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedOrder(null);
  };

  const exportToCSV = () => {
    const data = tabValue === 0 ? filteredBookings : filteredMedicineOrders;
    const headers =
      tabValue === 0
        ? [
            "Order ID",
            "Service Type",
            "Guest Name",
            "Email",
            "Phone",
            "Date",
            "Amount",
            "Payment Status",
            "Cancellation Reason",
          ]
        : [
            "Order ID",
            "Order Type",
            "Customer Name",
            "Phone",
            "Email",
            "Total Items",
            "Total Amount",
            "Payment Status",
            "Created At",
          ];

    let csvContent = headers.join(",") + "\n";

    data.forEach((item) => {
      if (tabValue === 0) {
        csvContent += [
          item.id,
          item.service_type,
          item.guest_name,
          item.guest_email,
          item.guest_phone,
          formatDate(item.date),
          item.payment?.amount || 0,
          item.payment?.status || "N/A",
          `"${(item.cancellation_reason || "Not provided").replace(/"/g, '""')}"`,
        ].join(",") + "\n";
      } else {
        csvContent += [
          item.id,
          item.order_type,
          item.delivery_address?.full_name || "N/A",
          item.delivery_address?.phone || "N/A",
          item.user_email || "N/A",
          item.items?.length || 0,
          item.total_amount || 0,
          item.payment?.status || "N/A",
          formatDate(item.created_at),
        ].join(",") + "\n";
      }
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `cancelled_${tabValue === 0 ? "bookings" : "medicine_orders"}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="700" color={color}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: `${color}20`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "#F9FAFB",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#006D77" }} />
        <Typography sx={{ mt: 2, fontSize: 16, color: "#6B7280" }}>
          Loading all cancelled orders...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: 1, mt: 5,marginLeft:'300px' }}>    
      <Container maxWidth="xl">
        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight="700" color="#006D77" gutterBottom>
                Cancelled Orders Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track and manage all cancelled bookings and medicine orders across all users
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Tooltip title="Refresh Data">
                <IconButton
                  onClick={fetchAllCancelledOrders}
                  sx={{
                    bgcolor: "#006D77",
                    color: "white",
                    "&:hover": { bgcolor: "#005A63" },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to CSV">
                <IconButton
                  onClick={exportToCSV}
                  disabled={
                    (tabValue === 0 && filteredBookings.length === 0) ||
                    (tabValue === 1 && filteredMedicineOrders.length === 0)
                  }
                  sx={{
                    bgcolor: "#059669",
                    color: "white",
                    "&:hover": { bgcolor: "#047857" },
                    "&:disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" },
                  }}
                >
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Cancelled Bookings"
              value={cancelledBookings.length}
              icon={<LocalHospitalIcon sx={{ fontSize: 32, color: "#DC2626" }} />}
              color="#DC2626"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Cancelled Medicine Orders"
              value={cancelledMedicineOrders.length}
              icon={<MedicationIcon sx={{ fontSize: 32, color: "#D97706" }} />}
              color="#D97706"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue Lost"
              value={formatCurrency(
                [...cancelledBookings, ...cancelledMedicineOrders].reduce(
                  (sum, order) =>
                    sum + (order.payment?.amount || order.total_amount || 0),
                  0
                )
              )}
              icon={<PaymentIcon sx={{ fontSize: 32, color: "#7C3AED" }} />}
              color="#7C3AED"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Cancelled Orders"
              value={cancelledBookings.length + cancelledMedicineOrders.length}
              icon={<DescriptionIcon sx={{ fontSize: 32, color: "#2563EB" }} />}
              color="#2563EB"
            />
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by ID, name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Filter</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date Filter"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last 7 Days</MenuItem>
                  <MenuItem value="month">Last 30 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={paymentFilter}
                  label="Payment Status"
                  onChange={(e) => setPaymentFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery("");
                  setDateFilter("all");
                  setPaymentFilter("all");
                }}
                sx={{ height: 40 }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper elevation={2} sx={{ borderRadius: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              px: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 15,
              },
            }}
          >
            <Tab
              label={
                <Badge badgeContent={filteredBookings.length} color="error">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalHospitalIcon fontSize="small" />
                    Service Bookings
                  </Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={filteredMedicineOrders.length} color="warning">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MedicationIcon fontSize="small" />
                    Medicine Orders
                  </Box>
                </Badge>
              }
            />
          </Tabs>

          {/* Service Bookings Table */}
          {tabValue === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Service Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Guest Details</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cancellation Reason</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                          No cancelled bookings found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {cancelledBookings.length === 0 
                            ? "No cancelled bookings in the system"
                            : "Try adjusting your filters"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBookings.map((booking) => (
                      <TableRow
                        key={booking.id}
                        hover
                        sx={{ "&:hover": { bgcolor: "#F0F9FF" } }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            fontFamily="monospace"
                          >
                            {booking.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.service_type}
                            size="small"
                            sx={{
                              bgcolor: "#E0F2F4",
                              color: "#006D77",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {booking.guest_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.guest_email}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {booking.guest_phone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(booking.date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="700" color="#006D77">
                            {formatCurrency(booking.payment?.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.payment?.status || "N/A"}
                            size="small"
                            sx={{
                              bgcolor:
                                booking.payment?.status === "paid"
                                  ? "#D1FAE5"
                                  : booking.payment?.status === "refunded"
                                  ? "#FEF3C7"
                                  : "#FEE2E2",
                              color:
                                booking.payment?.status === "paid"
                                  ? "#059669"
                                  : booking.payment?.status === "refunded"
                                  ? "#D97706"
                                  : "#DC2626",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {booking.cancellation_reason || "Not provided"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(booking, "booking")}
                              sx={{ color: "#006D77" }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Medicine Orders Table */}
          {tabValue === 1 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Order Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer Details</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Items</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMedicineOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                          No cancelled medicine orders found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {cancelledMedicineOrders.length === 0 
                            ? "No cancelled medicine orders in the system"
                            : "Try adjusting your filters"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMedicineOrders.map((order) => (
                      <TableRow
                        key={order.id}
                        hover
                        sx={{ "&:hover": { bgcolor: "#FEF3C7" } }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            fontFamily="monospace"
                          >
                            {order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.order_type}
                            size="small"
                            sx={{
                              bgcolor: "#FEF3C7",
                              color: "#D97706",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {order.delivery_address?.full_name || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.delivery_address?.phone || "N/A"}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {order.user_email || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${order.items?.length || 0} items`}
                            size="small"
                            sx={{
                              bgcolor: "#E0E7FF",
                              color: "#4F46E5",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="700" color="#006D77">
                            {formatCurrency(order.total_amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.payment?.status || "N/A"}
                            size="small"
                            sx={{
                              bgcolor:
                                order.payment?.status === "paid"
                                  ? "#D1FAE5"
                                  : order.payment?.status === "refunded"
                                  ? "#FEF3C7"
                                  : "#FEE2E2",
                              color:
                                order.payment?.status === "paid"
                                  ? "#059669"
                                  : order.payment?.status === "refunded"
                                  ? "#D97706"
                                  : "#DC2626",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(order.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(order, "medicine")}
                              sx={{ color: "#D97706" }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Detail Dialog - Same as before */}
        <Dialog
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#006D77",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="700">
              Order Details
            </Typography>
            <IconButton onClick={handleCloseDetailDialog} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedOrder && (
              <Box>
                {/* Booking Details */}
                {selectedOrder.orderType === "booking" && (
                  <Stack spacing={2}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Order Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Order ID
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            #{selectedOrder.id}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Service Type
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedOrder.service_type}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Date
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {formatDate(selectedOrder.date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={selectedOrder.status}
                            size="small"
                            sx={{
                              bgcolor: "#FEE2E2",
                              color: "#DC2626",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Guest Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedOrder.guest_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Age & Gender
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedOrder.age}y • {selectedOrder.gender}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedOrder.guest_email}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedOrder.guest_phone}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Payment Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Amount
                          </Typography>
                          <Typography variant="h6" fontWeight="700" color="#006D77">
                            {formatCurrency(selectedOrder.payment?.amount || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Method
                          </Typography>
                          <Typography variant="body1" fontWeight="600" textTransform="capitalize">
                            {selectedOrder.payment?.method || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={selectedOrder.payment?.status || "N/A"}
                            size="small"
                            sx={{
                              bgcolor:
                                selectedOrder.payment?.status === "paid"
                                  ? "#D1FAE5"
                                  : "#FEE2E2",
                              color:
                                selectedOrder.payment?.status === "paid"
                                  ? "#059669"
                                  : "#DC2626",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, bgcolor: "#FEF2F2" }}>
                      <Typography variant="subtitle2" color="#DC2626" gutterBottom fontWeight="700">
                        Cancellation Reason
                      </Typography>
                      <Typography variant="body1">
                        {selectedOrder.cancellation_reason || "Not provided"}
                      </Typography>
                    </Paper>

                    {selectedOrder.notes && (
                      <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Notes
                        </Typography>
                        <Typography variant="body1">{selectedOrder.notes}</Typography>
                      </Paper>
                    )}
                  </Stack>
                )}

                {/* Medicine Order Details - Same as original */}
                {selectedOrder.orderType === "medicine" && (
                  <Stack spacing={2}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Order Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Order ID
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            #{selectedOrder.id}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Order Type
                          </Typography>
                          <Typography variant="body1" fontWeight="600" textTransform="capitalize">
                            {selectedOrder.order_type}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Created At
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {formatDate(selectedOrder.created_at)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Order Status
                          </Typography>
                          <Chip
                            label={selectedOrder.order_status}
                            size="small"
                            sx={{
                              bgcolor: "#FEE2E2",
                              color: "#DC2626",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Order Items
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Medicine</TableCell>
                              <TableCell align="right">Qty</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedOrder.items?.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>{item.medicine_name}</TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(item.discounted_price || item.price)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(item.total_price)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    {selectedOrder.delivery_address && (
                      <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Delivery Address
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedOrder.delivery_address.full_name}
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.delivery_address.address_line1}
                        </Typography>
                        {selectedOrder.delivery_address.address_line2 && (
                          <Typography variant="body2">
                            {selectedOrder.delivery_address.address_line2}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          {selectedOrder.delivery_address.city},{" "}
                          {selectedOrder.delivery_address.state} -{" "}
                          {selectedOrder.delivery_address.pincode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Phone: {selectedOrder.delivery_address.phone}
                        </Typography>
                      </Paper>
                    )}

                    <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Payment Summary
                      </Typography>
                      <Stack spacing={1}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2">Subtotal</Typography>
                          <Typography variant="body2" fontWeight="600">
                            {formatCurrency(selectedOrder.subtotal || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2">Delivery Charge</Typography>
                          <Typography variant="body2" fontWeight="600">
                            {formatCurrency(selectedOrder.delivery_charge || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2">Discount</Typography>
                          <Typography variant="body2" fontWeight="600" color="#059669">
                            -{formatCurrency(selectedOrder.discount || 0)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="h6" fontWeight="700">
                            Total Amount
                          </Typography>
                          <Typography variant="h6" fontWeight="700" color="#006D77">
                            {formatCurrency(selectedOrder.total_amount || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2">Payment Status</Typography>
                          <Chip
                            label={selectedOrder.payment?.status || "N/A"}
                            size="small"
                            sx={{
                              bgcolor:
                                selectedOrder.payment?.status === "paid"
                                  ? "#D1FAE5"
                                  : "#FEE2E2",
                              color:
                                selectedOrder.payment?.status === "paid"
                                  ? "#059669"
                                  : "#DC2626",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </Box>
                      </Stack>
                    </Paper>

                    {selectedOrder.cancellation_info &&
                      Object.keys(selectedOrder.cancellation_info).length > 0 && (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: "#FEF2F2" }}>
                          <Typography
                            variant="subtitle2"
                            color="#DC2626"
                            gutterBottom
                            fontWeight="700"
                          >
                            Cancellation Information
                          </Typography>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap" }}>
                            {JSON.stringify(selectedOrder.cancellation_info, null, 2)}
                          </Typography>
                        </Paper>
                      )}

                    {selectedOrder.prescription_url && (
                      <Paper elevation={0} sx={{ p: 2, bgcolor: "#F9FAFB" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Prescription
                        </Typography>
                        <Button
                          variant="outlined"
                          href={selectedOrder.prescription_url}
                          target="_blank"
                          startIcon={<DescriptionIcon />}
                        >
                          View Prescription
                        </Button>
                      </Paper>
                    )}
                  </Stack>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDetailDialog} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CancelledOrders;